// @ts-nocheck
import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { HTTPClient } from "../../HTTPClient";
import type { Gift } from "../../types/campaign/Gift";
import type { CampaignContextType } from "../../types/campaign/CampaignContext";
import { useSnackbar } from "../../providers/ToastProvider";
import { useTranslation } from "react-i18next";
import { GiftType } from "@tgc/types";
import type { Action, Campaign, CampaignConfiguration } from "@tgc/types";
import { isActionValid } from "../../utils/actions";
import { usePlace } from "../../providers/PlaceProvider";
import type { PromoCode } from "../../types/campaign/PromoCode";
import type { ProxyCampaignTarget } from "../../types/campaign/ProxyCampaignTargets";

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

const CampaignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [skipCooldown, setSkipCooldown] = useState<boolean>(false);
  const { t } = useTranslation("", { keyPrefix: "views.campaign.save" });
  const { palette } = useTheme();
  const { snackbarShowMessage } = useSnackbar();
  const { selectedPlace } = usePlace();

  const [id, setID] = useState<string>(selectedPlace?.id || "");
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [campaign, setCampaign] = useState<CampaignConfiguration | null>(null);
  const [targets, setTargets] = useState<ProxyCampaignTarget>([]);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode>([]);
  const [game, setGame] = useState<string>("WHEEL");
  const [primaryColor, setPrimaryColor] = useState<string>(palette.primary.main);
  const [secondaryColor, setSecondaryColor] = useState<string>(palette.secondary.main);
  const [logo, setLogo] = useState<string>("");
  const [logoUri, setLogoUri] = useState<string>("");
  const [startDate, setStartDate] = useState<string | undefined>();
  const [label, setLabel] = useState<string>("");
  const [placeId, setPlaceId] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [profile, setProfile] = useState<string>("PREMIUM");
  const [theme, setTheme] = useState<string>("DEFAULT");
  const [kioskMode, setKioskMode] = useState<boolean>(false);
  const [wizardMode, setWizardMode] = useState<boolean>(false);
  const [voucherExpiration, setVoucherExpiration] = useState<number | undefined>(undefined);
  const [voucherStart, setVoucherStart] = useState<number>(1);

  const createCampaignConfigurationFromData = (data: Campaign) => {
    const { configuration } = data;
    setID(data.id || "");
    setCampaign(configuration);
    setSkipCooldown(data.skipCooldown ?? false);
    setTargets(configuration.targets ?? null);
    setGifts(configuration.gifts ?? []);
    setActions(configuration.actions ?? []);
    setPromoCodes(data.promoCodes ?? []);
    setGame(configuration.game_type ?? "WHEEL");
    setPrimaryColor(configuration.colors?.primary ?? palette.primary.main);
    setSecondaryColor(configuration.colors?.secondary ?? palette.secondary.main);
    setLogoUri(configuration.logo_uri ?? "");
    setStartDate(configuration.start_date);
    setLabel(data.label ?? "");
    setPlaceId(data.placeId ?? "");
    setIsDisabled(configuration.disabled ?? false);
    setProfile(configuration.profile ?? "PREMIUM");
    setTheme(configuration.theme ?? "DEFAULT");
    setKioskMode(configuration.kiosk_mode ?? false);
    setWizardMode(!configuration.actions?.length && !configuration.gifts?.length);
    const legacyCanUseVoucherImmediately =
      data.canUseVoucherImmediately ?? configuration.canUseVoucherImmediately;
    const configurationVoucherStart = configuration.voucherStart;
    if (legacyCanUseVoucherImmediately === true) {
      setVoucherStart(0);
    } else if (typeof configurationVoucherStart === "number") {
      setVoucherStart(configurationVoucherStart);
    } else {
      setVoucherStart(1);
    }
    setVoucherExpiration(data.voucherExpiration ?? configuration.voucherExpiration ?? undefined);
  };

  const saveCampaign = ({
    wizardStep,
    forceSave,
  }: { wizardStep?: number; forceSave?: boolean } = {}) => {
    return new Promise<Campaign | undefined>((resolve, reject) => {
      setIsSaving(true);

      if (!id) {
        snackbarShowMessage(t("errors.must-add-place"), "error");
        setIsSaving(false);
        return reject();
      }
      const steps = ["actions", "game", "customization", "gifts", "date"];

      if (!forceSave) {
        // always check actions, if wizard only check if... (?)
        const shouldCheck = (step: string) =>
          wizardStep !== undefined ? wizardStep >= steps.indexOf(step) : true;

        if (!actions.length && !campaign?.is_proxy) {
          snackbarShowMessage(t("errors.actions.empty"), "error");
          setIsSaving(false);
          return reject();
        }
        if (actions.some((a) => !isActionValid(a))) {
          snackbarShowMessage(t("errors.actions.target_missing"), "error");
          setIsSaving(false);
          return reject();
        }
        if (!gifts.length && shouldCheck("gifts")) {
          snackbarShowMessage(t("errors.gifts.empty"), "error");
          setIsSaving(false);
          return reject();
        }
        if (
          gifts.some(
            (g) => g.type !== "LOSS" && (!g.name || !g.type || typeof g.limit !== "number")
          ) &&
          shouldCheck("gifts")
        ) {
          snackbarShowMessage(t("errors.gifts.empty_fields"), "error");
          setIsSaving(false);
          return reject();
        }
        if (
          !gifts.some((g) => g.type === "LOSS") &&
          !gifts.some((g) => g.limit && g.limit <= -1) &&
          shouldCheck("gifts")
        ) {
          snackbarShowMessage(t("errors.gifts.missing_unlimited"), "error");
          setIsSaving(false);
          return reject();
        }
        const hasPromoCodeGift = gifts.some((g) => g.type === GiftType.PROMO_CODE);
        const hasAtLeastOnePromoCode = promoCodes.some(
          (promoCode) => promoCode?.value && `${promoCode.value}`.trim().length > 0
        );
        if (hasPromoCodeGift && !hasAtLeastOnePromoCode && shouldCheck("gifts")) {
          snackbarShowMessage(t("errors.promoCodes.empty"), "error");
          setIsSaving(false);
          return reject();
        }

        if (!startDate && shouldCheck("date") && wizardStep !== undefined) {
          snackbarShowMessage(t("errors.date.empty"), "error");
          setIsSaving(false);
          return reject();
        }
      }

      // voucherStart and voucherExpiration are independent
      const payloadVoucherExpiration =
        voucherExpiration ?? campaign?.voucherExpiration ?? undefined;
      const payloadVoucherStart = voucherStart ?? campaign?.voucherStart ?? 1;

      console.log("Gifts saved:", gifts);
      HTTPClient.post(`/campaign/`, {
        id,
        gifts: gifts.map((gift) => ({
          ...gift,
          initial_limit: gift.id?.startsWith("new-") ? gift.initial_limit : gift.initial_limit,
        })),
        actions: actions.map(({ ...action }) => ({
          ...action,
        })),
        promoCodes:
          promoCodes.map(({ ...promoCode }) => ({
            ...promoCode,
          })) ?? [],
        game_type: game,
        primaryColor,
        secondaryColor,
        logo_uri: logoUri,
        label,
        start_date: startDate,
        disabled: isDisabled,
        profile,
        theme,
        kiosk_mode: kioskMode,
        is_proxy: campaign?.is_proxy ?? false,
        ...(campaign?.is_proxy && { targets: targets }),
        is_child: campaign?.is_child ?? false,
        skipCooldown,
        voucherExpiration: payloadVoucherExpiration,
        voucherStart: payloadVoucherStart,
      })
        .then(({ data }) => {
          resolve(data);
          snackbarShowMessage(t("success"));
        })
        .catch((err) => {
          if (err.response?.data?.code === "VOUCHER_START_INVALID") {
            snackbarShowMessage(t("errors.voucher_start_invalid"), "error");
            return;
          }
          // if 413  Content too large
          if (err.response?.status === 413) {
            snackbarShowMessage(t("errors.content_too_large"), "error");
            return;
          }
          if (err.response?.status === 403) {
            snackbarShowMessage(t("errors.forbidden"), "error");
            return;
          }
          reject();
          snackbarShowMessage(t("unknown-error"), "error");
        })
        .finally(() => {
          setIsSaving(false);
        });
    });
  };

  const fetchCampaign = () => {
    setIsLoaded(false);
    if (!selectedPlace) return;
    // console.log("Fetching campaign: " + selectedPlace.id);
    HTTPClient.get("/campaign/" + selectedPlace.id).then(({ data }: { data: Campaign }) => {
     
      setIsLoaded(true);
      createCampaignConfigurationFromData(data);
    });
  };

  const refresh = () => {
    fetchCampaign();
  };

  useEffect(() => {
    // console.log("Triggered fetchCampaign");
    fetchCampaign();
  }, [selectedPlace?.id]);

  const contextValue: CampaignContextType = {
    skipCooldown,
    setSkipCooldown,
    id,
    campaign,
    gifts,
    actions,
    promoCodes,
    game,
    primaryColor,
    secondaryColor,
    logo,
    logoUri,
    startDate,
    label,
    placeId,
    isLoaded,
    isSaving,
    isDisabled,
    profile,
    wizardMode,
    voucherExpiration,
    voucherStart,
    setID,
    setGifts,
    setActions,
    setPromoCodes,
    setGame,
    setPrimaryColor,
    setSecondaryColor,
    setLogo,
    setLogoUri,
    setStartDate,
    setLabel,
    setIsLoaded,
    setIsDisabled,
    setProfile,
    setCampaign,
    setVoucherExpiration,
    setVoucherStart,
    saveCampaign,
    refresh,
  };

  return <CampaignContext.Provider value={contextValue}>{children}</CampaignContext.Provider>;
};

const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error("useCampaign must be used within a CampaignProvider");
  }
  return context;
};

export { CampaignProvider, useCampaign };
